import ScCheckbox from "@/src/components/ui-kit/checkbox";
import BlocksAvatar from "@/src/components/ui-kit/blocks-avatar";
import { EventParticipantSnapshot } from "@/src/models/event.model";
import { HoverTooltip } from "@/src/components/ui-kit/hover-tooltip";
import { useTheme } from "@/src/styles/theme-provider";
import { useTranslation } from "react-i18next";
import { Pressable, Text, View } from "react-native";

type ParticipantTableHeaderProps = Readonly<{
  isAuthor: boolean;
  isAllSelected: boolean;
  onToggleAll: () => void;
}>;

export const ParticipantTableHeader = ({
  isAuthor,
  isAllSelected,
  onToggleAll,
}: ParticipantTableHeaderProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  return (
    <View className="flex-row items-center py-2 border-b border-ash-3">
      {isAuthor && (
        <View className="w-7 items-center justify-center">
          <ScCheckbox value={isAllSelected} onChange={onToggleAll} />
        </View>
      )}
      <View className="w-9" />
      <Text
        className="flex-1 text-xs font-medium pl-2"
        style={{ color: themeVars["--color-ash-1"] as string }}
      >
        {t("LABEL_COL_NAME")}
      </Text>
      <Text
        className="flex-1 text-xs font-medium"
        style={{ color: themeVars["--color-ash-1"] as string }}
      >
        {t("LABEL_COL_EMAIL")}
      </Text>
      <Text
        className="w-12 text-xs font-medium"
        style={{ color: themeVars["--color-ash-1"] as string }}
      >
        {t("LABEL_COL_GUEST")}
      </Text>
    </View>
  );
};

type ParticipantRowProps = Readonly<{
  participant: EventParticipantSnapshot;
  userId?: string;
  selected: boolean;
  isAuthor: boolean;
  onToggle: () => void;
  /** Guests this attendee is bringing. Names show on hover over the count. */
  guestNames?: string[];
}>;

export const ParticipantRow = ({
  participant,
  userId,
  selected,
  isAuthor,
  onToggle,
  guestNames = [],
}: ParticipantRowProps) => {
  const { themeVars } = useTheme();
  const guestCount = guestNames.length;
  const guestList = (
    <View>
      {guestNames.map((name, index) => (
        <Text key={`${name}-${index}`} className="text-chalk text-xs">
          {name}
        </Text>
      ))}
    </View>
  );
  return (
    <Pressable
      onPress={isAuthor ? onToggle : undefined}
      className="flex-row items-center py-3 active:bg-ash-3 web:hover:bg-ash-3"
    >
      {isAuthor && (
        <View className="w-7 items-center justify-center">
          <ScCheckbox value={selected} onChange={onToggle} />
        </View>
      )}
      <BlocksAvatar
        userId={userId}
        fallbackImageUrl={participant?.profileImageUrl}
        size={36}
        className="bg-ash-3"
      />
      <HoverTooltip
        className="flex-1"
        delay={200}
        disabled={guestCount === 0}
        content={guestList}
      >
        <Text
          className="text-sm font-medium pl-2"
          style={{ color: themeVars["--color-onyx-default"] as string }}
          numberOfLines={1}
        >
          {[participant?.firstName, participant?.lastName]
            .filter(Boolean)
            .join(" ")}
        </Text>
      </HoverTooltip>
      <Text
        className="flex-1 text-sm"
        style={{ color: themeVars["--color-ash-1"] as string }}
        numberOfLines={1}
      >
        {participant?.email}
      </Text>
      {/* Guest count, with the names on hover. Hover is pointer-only, so the
          count itself carries the information on touch devices. */}
      <View className="w-12">
        {guestCount > 0 ? (
          <HoverTooltip delay={200} content={guestList}>
            <Text
              className="text-sm font-medium"
              style={{ color: themeVars["--color-onyx-default"] as string }}
              accessibilityLabel={guestNames.join(", ")}
            >
              {guestCount}
            </Text>
          </HoverTooltip>
        ) : (
          <Text
            className="text-sm"
            style={{ color: themeVars["--color-ash-1"] as string }}
          >
            0
          </Text>
        )}
      </View>
    </Pressable>
  );
};
